import { z } from "zod/v4";

/**
 * The 15-minute slot an on-request site export covers up to. TrafficFlow
 * stores 15-minute buckets, so an export built in a slot is reused for every
 * repeat request in that slot.
 *
 * The slot's cutoff doubles as the export's id: `YYYYMMDDTHHmmZ` (UTC), e.g.
 * `20261103T0345Z`. It is also an S3 key segment, so the id is only ever
 * accepted when it names a real slot.
 */

const SLOT_MS = 15 * 60 * 1000;

const EXPORT_ID_PATTERN = /^(\d{4})(\d{2})(\d{2})T(\d{2})(00|15|30|45)Z$/;

/** `now` floored to the start of its 15-minute UTC slot. */
export function floorToExportSlot(now: Date): Date {
  return new Date(Math.floor(now.getTime() / SLOT_MS) * SLOT_MS);
}

/** The export id of a slot's cutoff. */
export function exportIdOf(cutoff: Date): string {
  const iso = cutoff.toISOString();
  return `${iso.slice(0, 4)}${iso.slice(5, 7)}${iso.slice(8, 10)}T${iso.slice(11, 13)}${iso.slice(14, 16)}Z`;
}

/** The cutoff an export id names, or `undefined` when it names no real slot (e.g. 30 Feb, 24:00, 10:07). */
function cutoffOf(exportId: string): Date | undefined {
  const match = EXPORT_ID_PATTERN.exec(exportId);
  if (match === null) return undefined;
  const [, year, month, day, hour, minute] = match;
  const cutoff = new Date(`${year ?? ""}-${month ?? ""}-${day ?? ""}T${hour ?? ""}:${minute ?? ""}:00.000Z`);
  // `Date` rolls an impossible day over, so only a round trip proves the id names a real slot.
  return !Number.isNaN(cutoff.getTime()) && exportIdOf(cutoff) === exportId ? cutoff : undefined;
}

/** An export id that names a real 15-minute slot. */
export const ExportId = z.string().refine((id) => cutoffOf(id) !== undefined, {
  message: "Must be a 15-minute UTC slot, YYYYMMDDTHHmmZ",
});

/** An export id ↔ the cutoff instant it names. */
export const ExportCutoff = z.codec(ExportId, z.date(), {
  decode: (exportId, ctx) => {
    const cutoff = cutoffOf(exportId);
    if (cutoff === undefined) {
      ctx.issues.push({ code: "custom", input: exportId, message: "Must be a 15-minute UTC slot" });
      return z.NEVER;
    }
    return cutoff;
  },
  encode: exportIdOf,
});
